import { useNavigate } from 'react-router-dom';
import {
  faBuilding,
  faKey,
  faLock,
  faMoon,
  faPalette,
  faSun,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@/components/ui/button';
import { useThemeContext } from '@/providers/ThemeProvider';
import { ROUTES } from '@/routes/routes';
import type { MeProfile } from '@/types/profile.types';
import { InfoRow, ProfileSectionCard } from './ProfileSectionCard';

function fmt(value: string | null): string | null {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? null
    : d.toLocaleString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
}

/** Security — the existing change-password flow; nothing else is offered until P3. */
export function SecuritySection({ me }: { me: MeProfile }) {
  const navigate = useNavigate();
  return (
    <ProfileSectionCard
      title="Security"
      description="Your password. Changing it signs you out everywhere else."
      icon={faLock}
      testId="profile-security"
      action={
        <Button
          type="button"
          size="sm"
          className="min-h-11 sm:min-h-8"
          onClick={() => navigate(ROUTES.CHANGE_PASSWORD)}
        >
          <FontAwesomeIcon icon={faKey} className="mr-1.5 h-3 w-3" />
          Change password
        </Button>
      }
    >
      <div className="space-y-2">
        <InfoRow label="Password last changed" value={fmt(me.passwordChangedAt) ?? 'Never'} />
        <InfoRow label="Last sign-in" value={fmt(me.lastLoginAt) ?? 'Never'} />
      </div>
    </ProfileSectionCard>
  );
}

/** Account — company, status, created. All read-only. */
export function AccountSection({ me }: { me: MeProfile }) {
  return (
    <ProfileSectionCard title="Account" icon={faBuilding} testId="profile-account">
      <div className="space-y-2">
        <InfoRow label="Company" value={me.tenantName} />
        <InfoRow label="Workspace" value={me.tenantSlug} />
        <InfoRow label="Status" value={me.isActive ? 'Active' : 'Suspended'} />
        <InfoRow label="Member since" value={fmt(me.createdAt)} />
      </div>
    </ProfileSectionCard>
  );
}

/** Preferences — the theme toggle that already existed; notification preferences are a later phase (OD-5). */
export function PreferencesSection() {
  const { resolvedMode, setMode } = useThemeContext();
  return (
    <ProfileSectionCard title="Preferences" icon={faPalette} testId="profile-preferences">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-sm font-medium text-foreground">Appearance</p>
          <p className="text-xs text-muted-foreground">Light or dark, on this device.</p>
        </div>
        <div className="flex gap-2" role="radiogroup" aria-label="Appearance">
          {(['light', 'dark'] as const).map((mode) => (
            <button
              key={mode}
              type="button"
              role="radio"
              aria-checked={resolvedMode === mode}
              onClick={() => setMode(mode)}
              className={`flex min-h-11 items-center gap-2 rounded-lg border px-3 text-sm transition-colors sm:min-h-9 ${
                resolvedMode === mode
                  ? 'border-primary bg-primary/10 text-primary'
                  : 'border-border text-muted-foreground hover:bg-muted'
              }`}
            >
              <FontAwesomeIcon icon={mode === 'light' ? faSun : faMoon} className="h-3.5 w-3.5" />
              {mode === 'light' ? 'Light' : 'Dark'}
            </button>
          ))}
        </div>
      </div>
    </ProfileSectionCard>
  );
}
